import onScan from 'onscan.js';
import { useCallback, useEffect } from 'react';

interface UseBarcodeScannerOptions {
  onScan: (barcode: string) => void;
  minLength?: number;
  enabled?: boolean;
}

/**
 * Hook for scanning barcodes using the onscan.js library.
 * Automatically attaches to the document and detects barcode scans.
 */
export const useBarcodeScanner = ({
  onScan: onScanCallback,
  minLength = 4,
  enabled = true,
}: UseBarcodeScannerOptions) => {
  const handleScan = useCallback(
    (code: string) => {
      if (enabled) {
        onScanCallback(code);
      }
    },
    [onScanCallback, enabled]
  );

  useEffect(() => {
    if (!enabled) return;

    onScan.attachTo(document, {
      minLength,
      onScan: handleScan,
      keyCodeMapper: (oEvent: KeyboardEvent) => {
        // Preserve hyphens/dashes in barcodes
        if (oEvent.key === '-') {
          return '-';
        }
        // Preserve underscores
        if (oEvent.key === '_') {
          return '_';
        }
        // Fall back to the default decoder for other key codes
        return (onScan as any).decodeKeyEvent(oEvent);
      },
    });

    return () => {
      onScan.detachFrom(document);
    };
  }, [handleScan, minLength, enabled]);

  return {
    enabled,
  };
};
